import { Card, CardActionArea, Stack, SxProps, Theme, Typography } from '@mui/material';
import { ELEMENT } from '@schooly/constants/test-ids';
import { FC, ReactNode } from 'react';

export type ListRowVariant = 'default' | 'compact' | 'expanded';

export interface ListRowProps {
  title: ReactNode;
  titleVariant?: 'h3' | 'h2';
  titleAdornment?: ReactNode;
  subtitle?: ReactNode;
  leadingContent?: ReactNode;
  rightContent?: ReactNode;
  actions?: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  variant?: ListRowVariant;
  /** 'start' = content starts from left (red line), 'end' = right-aligned (default) */
  rightContentAlign?: 'start' | 'end';
  sx?: SxProps<Theme>;
}

type VariantStyles = {
  padding: number;
  actionAreaAlign: string;
  height?: number;
  contentDirection: 'row' | 'column';
  contentAlign: string;
  contentGap: number;
  contentWidth: string;
  contentPt: number;
  titleWidth: number | string;
  titleMaxWidth: number | string;
  actionsMinWidth: number;
  leadingWidth: number;
};

const variantStyles: Record<ListRowVariant, VariantStyles> = {
  default: {
    padding: 1.5,
    actionAreaAlign: 'center',
    contentDirection: 'column',
    contentAlign: 'stretch',
    contentGap: 0.5,
    contentWidth: 'auto',
    contentPt: 0,
    titleWidth: 'auto',
    titleMaxWidth: 'none',
    actionsMinWidth: 58,
    leadingWidth: 56,
  },
  compact: {
    padding: 1,
    actionAreaAlign: 'center',
    height: 45,
    contentDirection: 'row',
    contentAlign: 'center',
    contentGap: 1,
    contentWidth: '100%',
    contentPt: 0.25,
    titleWidth: 300,
    titleMaxWidth: 'none',
    actionsMinWidth: 25,
    leadingWidth: 56,
  },
  expanded: {
    padding: 1,
    actionAreaAlign: 'flex-start',
    height: 76,
    contentDirection: 'column',
    contentAlign: 'flex-start',
    contentGap: 0.5,
    contentWidth: 'auto',
    contentPt: 1,
    titleWidth: 'auto',
    titleMaxWidth: 250,
    actionsMinWidth: 58,
    leadingWidth: 56,
  },
};

export const ListRow: FC<ListRowProps> = ({
  title,
  titleVariant = 'h3',
  titleAdornment,
  subtitle,
  leadingContent,
  rightContent,
  actions,
  onClick,
  disabled = false,
  variant = 'default',
  sx,
}) => {
  const isExpanded = variant === 'expanded';
  const styles = variantStyles[variant];

  return (
    <Card
      data-test-id={ELEMENT.ROW}
      sx={{
        borderRadius: 0,
        border: 'none',
        borderBottom: (theme) => `1px solid ${theme.palette.common.light2}`,
        ...(styles.height && { '.MuiCardActionArea-root': { height: styles.height } }),
        ...(disabled && {
          '*': { color: (theme) => theme.palette.common.grey, pointerEvents: 'none' },
          svg: { opacity: 0.5 },
        }),
        '&:hover': {
          backgroundColor: (theme) => theme.palette.common.lightBg,
          '.list-row-title': { color: (theme) => theme.palette.primary.main },
          '.list-row-title-secondary': { color: (theme) => theme.palette.common.grey2 },
          '.list-row-text-green': { color: (theme) => theme.palette.common.grey2 },
        },
        ...sx,
      }}
    >
      <CardActionArea
        onClick={onClick}
        disabled={disabled}
        sx={{
          padding: styles.padding,
          display: 'flex',
          alignItems: styles.actionAreaAlign,
        }}
      >
        {leadingContent && (
          <Stack
            flexShrink={0}
            width={styles.leadingWidth}
            alignSelf="center"
            className="list-row-leading"
            mr={2}
          >
            {leadingContent}
          </Stack>
        )}
        <Stack
          direction="row"
          alignItems={isExpanded ? 'flex-start' : 'center'}
          justifyContent="space-between"
          flex={1}
          minWidth={0}
          gap={2}
        >
          <Stack
            minWidth={0}
            flex={1}
            overflow="hidden"
            direction={styles.contentDirection}
            alignItems={styles.contentAlign}
            gap={styles.contentGap}
            pt={styles.contentPt}
          >
            <Stack
              direction="row"
              alignItems="center"
              minWidth={0}
              width={styles.titleWidth}
              maxWidth={styles.titleMaxWidth}
            >
              {typeof title === 'string' ? (
                <Typography
                  className="list-row-title"
                  variant={titleVariant}
                  color="common.grey2"
                  noWrap
                >
                  {title}
                </Typography>
              ) : (
                title
              )}
              {titleAdornment}
            </Stack>
            {subtitle}
          </Stack>

          {rightContent && (
            <Stack flexShrink={0} alignItems="flex-start" className="list-row-right-content">
              {rightContent}
            </Stack>
          )}
        </Stack>

        <Stack
          minWidth={styles.actionsMinWidth}
          className="list-row-actions"
          alignSelf="stretch"
          justifyContent="center"
        >
          {actions}
        </Stack>
      </CardActionArea>
    </Card>
  );
};
